import fuzzysort from "fuzzysort"
import PropTypes from "prop-types"
import React, { Component } from "react"

import BrandAndModel from "./BrandAndModel"
import styles from "./BrandAndModelSelector.module.sass"
import withBrandAndModel from "./withBrandAndModel"

const createModelObject = (obj) => {
  return {
    label: obj.model,
    value: obj,
  }
}

const KEY_CODE_BACKSPACE = 8

class ModelSelector extends Component {
  static propTypes = {
    autoFocus: PropTypes.bool.isRequired,
    topBrands: PropTypes.array.isRequired, // ["Audi", "Nissan", "Ford"]
    all: PropTypes.array.isRequired, // [["Audi", ["A3", "A4", "Q5"]], ["BMW", ["112"]]]
    doneCallback: PropTypes.func,
    initiallySelected: PropTypes.object,
    labels: PropTypes.object,
    visibleBrands: PropTypes.array.isRequired,
    theme: PropTypes.object.isRequired,
    onTypedTextChange: PropTypes.func.isRequired,
    onResultsChange: PropTypes.func.isRequired,
    onItemSelect: PropTypes.func.isRequired,
    textInputChange: PropTypes.func.isRequired,
    textInputBlur: PropTypes.func.isRequired,
    typedText: PropTypes.string.isRequired,
    inputFocused: PropTypes.bool.isRequired,
    results: PropTypes.array.isRequired,
    topModelsForBrand: PropTypes.object,
    selectedBrand: PropTypes.string,
  }

  constructor(props) {
    super(props)

    const initiallySelectedModel = this.initiallySelectedModel()
    const typedText = initiallySelectedModel.inputText || ""
    const results = this.initialResults(this.props.selectedBrand, typedText)

    this.state = {
      model: initiallySelectedModel.model,
    }

    props.onTypedTextChange(typedText)
    props.onResultsChange(results)
  }

  // Selects model based on initial props
  initiallySelectedModel() {
    const { initiallySelected } = this.props

    if (initiallySelected && initiallySelected.car_model) {
      return {
        model: initiallySelected.car_model,
        inputText: initiallySelected.car_model || "",
      }
    }

    return {}
  }

  initialResults(brand, typedText) {
    if (!brand) {
      throw new Error("Brand must be provided")
    }
    if (typedText) return this.searchResultsFor(typedText)

    return [
      { options: this.topModelOptionsForBrand(brand) },
      { category: "A-Z", options: this.allModelOptionsForBrand(brand) },
    ]
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.typedText !== prevState.typedText) {
      this.props.onResultsChange(this.searchResultsFor(prevState.typedText))
    }
  }

  textInputKeyDown = (e) => {
    if (e.keyCode === KEY_CODE_BACKSPACE && this.state.model) {
      this.setState({
        model: null,
      })
    }
  }

  topModelOptionsForBrand(brand) {
    const topModelsForBrand = this.props.topModelsForBrand[brand]

    if (!topModelsForBrand) return this.allModelOptionsForBrand(brand)

    return topModelsForBrand.map((model) => createModelObject({ brand, model }))
  }

  allModelOptionsForBrand(brand) {
    return this.props.all
      .find(([b]) => b === brand)[1]
      .map((model) => createModelObject({ brand, model }))
  }

  // Returns the search results for a given input text
  searchResultsFor(inputText) {
    // Searching in models of the selected brand
    const models = this.props.all
      .find(([b]) => b === this.props.selectedBrand)[1]
      .map((model) => ({ brand: this.props.selectedBrand, model }))
    if (inputText) {
      const results = fuzzysort
        .go(inputText, models, { key: "model" })
        .map(({ obj }) => createModelObject(obj))
      return [{ options: results }]
    }

    const allModelOptions = models.map((model) => createModelObject(model))
    const topModelsForBrand =
      this.props.topModelsForBrand[this.props.selectedBrand]
    if (!topModelsForBrand) return [{ options: allModelOptions }]

    return [
      { options: this.topModelOptionsForBrand(this.props.selectedBrand) },
      { category: "A-Z", options: allModelOptions },
    ]
  }

  render() {
    return (
      <BrandAndModel
        autoFocus={this.props.autoFocus}
        value={this.props.typedText}
        onChange={this.props.textInputChange}
        onBlur={this.props.textInputBlur}
        onKeyDown={this.textInputKeyDown}
        focused={this.props.inputFocused}
        classNameInput={styles.input}
        inputPlaceholder={this.props.labels.placeholders.brand}
        choices={this.props.results}
        selectItem={this.props.onItemSelect}
        theme={this.props.theme}
        labelSeeAll={this.props.labels.seeAll}
        labelClose={this.props.labels.close}
      />
    )
  }
}

export default withBrandAndModel(ModelSelector, "car_model", "model")
